import {NavigationContainer} from '@react-navigation/native';
import querystring from 'query-string';
import * as React from 'react';
import {useEffect, useRef, useState} from 'react';
import {AppState, Platform, StatusBar, StyleSheet, View} from 'react-native';
import {SafeAreaProvider} from 'react-native-safe-area-context';
import {createNavigationContainerRef} from '@react-navigation/native';
import {AppDispatch, store, useAppDispatch, useAppSelector} from '../../redux';
import {Dispatch} from 'redux';
import {enter, esiaLogin, esiaLoginFromActiveUser, logout, resetLoginState} from '../../redux/actions/login';
import {restorePendingCardActivation} from '../../redux/actions/cardActivation';
import {getECardInfo, getHolderProfile, getProfileNotifications, getProfileSettings} from '../../redux/actions/profile';
import {getContent} from '../../redux/actions/resources';
import {compareRoutesToHideGoToApp} from '../../redux/actions/runtime/menu';
import {profileSlice} from '../../redux/reducers/profile';
import {
  profileAuthenticationStateSelector,
  profileSessionExpiredSelector,
  profileTokenSelector,
} from '../../redux/selectors/profile';
import {PinCodeAuthentication} from '../../screens/login/pinCodeAuthentication';
import {useModal} from '../components/modal';
import {Preloader} from '../components/preloader';
import {codeLinkScheme} from '../constants';
import {fcmService, useFcmWrapper} from '../fcmWrapper';
import {useDimensions} from '../hooks/useDimensions';
import {throttle} from '../pipes';
import {storage, StorageKeys} from '../storage';
import {
  clearCardReissueEsiaPending,
  hydrateCardReissueEsiaContext,
  isCardReissueEsiaOAuthReturn,
  isCardReissueEsiaPending,
} from '../storage/cardReissueEsia';
import {hasEsiaOAuthParams} from '../utils/esiaOAuthParams';
import {hideGlobalLoader} from '../utils/globalLoader';
import {useTheme} from '../theme';
import {DeviceSize, useDeviceSize} from '../theme/helpers';
import {useGlobalScroll} from '../hooks/useGlobalScroll';
import AppRedirectForm from './app-redirect-form';
import {DesktopStackScreen} from './desktop';
import {DrawerMenu} from './drawer';
import {linkingDesktop, linkingLogin, linkingMobile} from './linking';
import {
  isChangeEmailConfirmation,
  parseConfirmationQuery,
  useConfirmationDeepLink,
} from '../hooks/useConfirmationDeepLink';
import {Routes} from './model';
import {isMobileWebBrowser} from '../utils/getDeviceType';
import {openPendingEmailConfirmation} from './profileEmailConfirmationNav';
import {subscribeEsiaBrowserBack} from './esiaBrowserBack';
import {
  clearCardReissueEsiaOnLeave,
  resumeCardReissueAfterEsia,
  restoreCardReissuePathAfterEsia,
} from './cardReissueEsia';
import {LoginScreens} from './login';
import {loginSlice} from '../../redux/reducers/login';
import {setRuntimeLoginStepState} from '../../redux/actions/runtime/loginStep';
import {LoginState} from '../models/login/loginStep';
import {esiaUserHasNoCardsSelector} from '../../redux/selectors/login';

const openPendingNavigationsDesktop = () => {
  resumeCardReissueAfterEsia();
};

const openPendingNavigationsMobile = () => {
  openPendingEmailConfirmation();
  resumeCardReissueAfterEsia();
};

const isConfirmationPathname = (pathname: string) =>
  pathname === Routes.confirmation || pathname === `${Routes.confirmation}/`;

const getChangeEmailAppLink = () => {
  const search = window.location.search.startsWith('?') ? window.location.search : `?${window.location.search}`;

  return `confirmation${search}`;
};

export const rootNavigationRef = createNavigationContainerRef();

const maxNonBlockingTime = 60 * 1000;

const clearQueryParams = () => {
  if (Platform.OS !== 'web') return;
  try {
    const {origin, pathname} = window.location;
    window.history.replaceState(window.history.state, '', `${origin}${pathname}`);
  } catch (error) {
    console.error(error);
  }
};

interface DeepLinkInput {
  url: string | null;
}

export const Navigation = () => {
  const dispatch = useAppDispatch();
  const modals = useModal();
  useFcmWrapper();

  const [isAppDataRestored, setAppDataRestored] = useState(false);

  useEffect(() => {
    (async () => {
      await hydrateCardReissueEsiaContext();
      if (Platform.OS === 'web' && !hasEsiaOAuthParams()) {
        await clearCardReissueEsiaPending();
      }

      const token = await storage.getItem<string>(StorageKeys.token);
      const pinCode = await storage.getItem<string>(StorageKeys.pinCode);
      if (pinCode) {
        dispatch(profileSlice.actions.setPinCode(pinCode));
      }
      if (token) {
        if (pinCode || Platform.OS === 'web') {
          dispatch(enter(token));
        } else {
          dispatch(logout());
        }
      }
      setAppDataRestored(true);
    })();
  }, []);

  useEffect(() => subscribeEsiaBrowserBack(dispatch, () => store.getState().runtime.loginStep.value), [dispatch]);

  const token = useAppSelector(profileTokenSelector());
  const isSessionExpired = useAppSelector(profileSessionExpiredSelector());
  const isAuthenticated = useAppSelector(profileAuthenticationStateSelector());
  const isAuthenticatedByPassword = token && !isSessionExpired;
  const userHasNoCards = useAppSelector(esiaUserHasNoCardsSelector);
  const backgroundStartTimestamp = useRef<null | number>(null);

  useEffect(() => {
    if (Platform.OS === 'web' && isAuthenticatedByPassword && !isAuthenticated) {
      dispatch(profileSlice.actions.setAuthenticationState(true));
    }
  }, [isAuthenticatedByPassword, isAuthenticated, dispatch]);

  if (Platform.OS !== 'web') {
    if (backgroundStartTimestamp.current && AppState.currentState === 'active') {
      if (Date.now() - backgroundStartTimestamp.current > maxNonBlockingTime) {
        dispatch(profileSlice.actions.setAuthenticationState(false));
      }

      backgroundStartTimestamp.current = null;
    }

    if (!backgroundStartTimestamp.current && AppState.currentState !== 'active') {
      backgroundStartTimestamp.current = Date.now();
    }
  }

  // This ref is used to prevent loosing query params on page reload
  const initialOAuthQueryRef = useRef<string>('');

  if (Platform.OS === 'web' && !initialOAuthQueryRef.current) {
    initialOAuthQueryRef.current = window.location.search.replace('?', '');
  }

  useConfirmationDeepLink({
    dispatch,
    isAppDataRestored,
    isAuthenticated,
    isAuthenticatedByPassword: !!isAuthenticatedByPassword,
  });

  useEffect(() => {
    let modalId: string | null = null;

    if (Platform.OS !== 'web' && isAuthenticatedByPassword && !isAuthenticated) {
      modalId = modals.show({
        render: () => <PinCodeAuthentication />,
        mobile: true,
      });
    }

    return () => {
      !!modalId && modals.remove(modalId);
    };
  }, [isAuthenticatedByPassword, isAuthenticated]);

  // Re-check pending activation when entering/returning to an open cabinet session (other device submitted).
  useEffect(() => {
    if (!isAuthenticatedByPassword || !isAuthenticated) {
      return;
    }

    const checkPendingActivation = () => {
      void dispatch(restorePendingCardActivation());
    };

    checkPendingActivation();

    if (Platform.OS === 'web') {
      const onVisibilityChange = () => {
        if (document.visibilityState === 'visible') {
          checkPendingActivation();
        }
      };
      document.addEventListener('visibilitychange', onVisibilityChange);
      return () => document.removeEventListener('visibilitychange', onVisibilityChange);
    }

    const subscription = AppState.addEventListener('change', (nextState) => {
      if (nextState === 'active') {
        checkPendingActivation();
      }
    });
    return () => subscription.remove();
  }, [dispatch, isAuthenticated, isAuthenticatedByPassword]);

  // This ref is used to prevent multiple calls of useEffect
  const oAuthQueryParamsRef = useRef<string | null>(null);

  useEffect(() => {
    if (!isAppDataRestored || Platform.OS !== 'web') {
      return;
    }

    if (isMobileWebBrowser() && isConfirmationPathname(window.location.pathname)) {
      const {op, code} = parseConfirmationQuery(window.location.search);

      if (isChangeEmailConfirmation(op, code)) {
        window.location.replace(`${codeLinkScheme}?link=${encodeURIComponent(getChangeEmailAppLink())}`);
        return;
      }
    }

    if (window.location.pathname === codeLinkScheme) {
      return;
    }

    const currentQuery = window.location.search.replace('?', '');
    const query = currentQuery || initialOAuthQueryRef.current || '';
    const {code, state, op} = querystring.parse(query) ?? {};

    if (op) {
      initialOAuthQueryRef.current = '';
      return;
    }

    if (!code || !state) {
      if (!userHasNoCards) {
        dispatch(resetLoginState());
        dispatch(setRuntimeLoginStepState(LoginState.enter));
      }
      initialOAuthQueryRef.current = '';
      clearQueryParams();
      return;
    }

    if (oAuthQueryParamsRef.current !== query) {
      oAuthQueryParamsRef.current = query;
      if (isCardReissueEsiaPending() && !isCardReissueEsiaOAuthReturn(query)) {
        initialOAuthQueryRef.current = '';
        clearQueryParams();
        hideGlobalLoader({resetBlockingCalls: true});
        return;
      }
      dispatch(loginSlice.actions.setEsiaCode({code, state}));
      resumeCardReissueAfterEsia();
      if (isAuthenticated || isAuthenticatedByPassword) {
        dispatch(esiaLoginFromActiveUser());
      } else {
        dispatch(esiaLogin());
      }
      dispatch(setRuntimeLoginStepState(LoginState.enter));
      initialOAuthQueryRef.current = '';
      clearQueryParams();
    }
  }, [dispatch, isAppDataRestored, isAuthenticated, isAuthenticatedByPassword, token, userHasNoCards]);

  if (Platform.OS === 'web' && isMobileWebBrowser() && isConfirmationPathname(window.location.pathname)) {
    const {op, code} = parseConfirmationQuery(window.location.search);

    if (isChangeEmailConfirmation(op, code)) {
      return <AppRedirectForm link={getChangeEmailAppLink()} />;
    }
  }

  if (!isAppDataRestored) {
    return null;
  }

  restoreCardReissuePathAfterEsia();

  if (Platform.OS === 'web' && window?.location.pathname === codeLinkScheme) {
    const {link} = querystring.parse(window.location.search.replace('?', '')) ?? {};

    if (typeof link === 'string' && link.length) {
      return <AppRedirectForm link={link} />;
    }
  }

  if (!isAuthenticatedByPassword) {
    return <LoginContainer />;
  }

  return <AuthorizedContainer />;
};

export const initialLoading = async (dispatch: AppDispatch) => {
  // Multi-device: pending issuance/reissue on server must leave cabinet.
  const switchedToActivation = await dispatch(restorePendingCardActivation());
  if (switchedToActivation) {
    return;
  }

  await Promise.all([
    dispatch(getHolderProfile()),
    dispatch(getProfileSettings()),
    // dispatch(getProfilePhoto()),
    dispatch(getProfileNotifications()),
    dispatch(getECardInfo()),
  ]);
  // eslint-disalbe-next-line
  await dispatch(getContent());
};

export const syncFCMState = async (dispatch: Dispatch<any>) => {
  const isFcmConnected = await fcmService.isConnected();
  if (!isFcmConnected && Platform.OS === 'web') {
    throttle(async () => {
      // add here all methods to perform manual refresh
      dispatch(getProfileNotifications());
    }, 2000);
  }
};

const onNavigatorStateChange = (dispatch: Dispatch<any>) => {
  clearCardReissueEsiaOnLeave();
  syncFCMState(dispatch);
};

const DesktopNavigation = () => {
  const theme = useTheme();
  const {height} = useDimensions();
  const dispatch = useAppDispatch();
  const {scrollEnabled} = useGlobalScroll();

  return (
    <View style={{height, overflow: scrollEnabled ? 'visible' : 'hidden'}}>
      <NavigationContainer
        ref={rootNavigationRef}
        theme={theme.navigation}
        linking={linkingDesktop}
        onReady={openPendingNavigationsDesktop}
        onStateChange={() => onNavigatorStateChange(dispatch)}>
        <DesktopStackScreen />
      </NavigationContainer>
    </View>
  );
};

const MobileNavigation = () => {
  const theme = useTheme();
  const [isInitRender, setIsInitRender] = useState(true);
  const dispatch = useAppDispatch();

  useEffect(() => {
    if (Platform.OS === 'android') {
      StatusBar.setBackgroundColor(theme.color.grayMenu, true);
    }
    StatusBar.setBarStyle('dark-content', true);
  }, []);

  useEffect(() => {
    setIsInitRender(false);
  }, [isInitRender]);

  return (
    <SafeAreaProvider style={StyleSheet.absoluteFill}>
      <View
        style={{
          flex: 1,
          display: isInitRender ? 'none' : undefined,
          overflow: 'hidden',
        }}>
        <NavigationContainer
          ref={rootNavigationRef}
          theme={theme.navigation}
          linking={linkingMobile}
          fallback={<Preloader type='main' />}
          onReady={openPendingNavigationsMobile}
          onStateChange={(e) => {
            dispatch(compareRoutesToHideGoToApp(e));
            onNavigatorStateChange(dispatch);
          }}>
          <DrawerMenu />
        </NavigationContainer>
      </View>
    </SafeAreaProvider>
  );
};

const LoginContainer = () => {
  const {height} = useDimensions();

  useEffect(() => {
    if (Platform.OS === 'android') {
      StatusBar.setBackgroundColor('#0070B3', true);
    }
    StatusBar.setBarStyle('light-content', true);
  }, []);

  return (
    <View style={{height}}>
      <NavigationContainer linking={linkingLogin} fallback={<Preloader type='login' />}>
        <LoginScreens />
      </NavigationContainer>
    </View>
  );
};

const AuthorizedContainer = () => {
  const size = useDeviceSize();

  switch (size) {
    case DeviceSize.tablet:
    case DeviceSize.mobile:
      return <MobileNavigation />;
    default:
      return <DesktopNavigation />;
  }
};
